import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Eyebrow } from "@/components/SectionHeader";
import {
  ShieldCheck,
  ArrowLeftRight,
  Sprout,
  ArrowUpRight,
} from "lucide-react";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Resilience",
    body: "No single point of dependency for your payment reach. Multiple network partners mean multiple paths for every corridor.",
  },
  {
    icon: ArrowLeftRight,
    title: "Choice",
    body: "Routing decisions optimized for corridor, cost, and speed. Your integration flexes with your business—not the other way around.",
  },
  {
    icon: Sprout,
    title: "Continuous expansion",
    body: "As we add new network relationships, your access grows automatically. New corridors, currencies and methods without new integrations.",
  },
];

const PARTNERS = [
  "Global Wallet Network",
  "APAC Bank Rail",
  "European Instant",
  "US Wire Consortium",
  "MENA Payout Grid",
  "LATAM Corridor Co.",
  "Stablecoin Bridge",
  "Card Scheme Partner",
];

export default function Partnerships() {
  return (
    <div>
      {/* Hero */}
      <section className="pt-40 pb-16 md:pb-24 bg-[color:var(--p10-ivory)] border-b border-[color:var(--p10-line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Eyebrow>Network partnerships</Eyebrow>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.98] mt-6 max-w-5xl"
          >
            Connected to the networks that move{" "}
            <em className="text-[color:var(--p10-royal)]">
              the world's money.
            </em>
          </motion.h1>

          <p className="mt-8 max-w-2xl text-lg text-[color:var(--p10-ink)]/70 leading-relaxed">
            Pay10x integrates with leading global cross-border payment networks
            across mobile wallets, bank accounts, cards and emerging payout
            rails such as stablecoins.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--p10-line)] border border-[color:var(--p10-line)]">
            {PILLARS.map((pillar, index) => {
              const Icon = pillar.icon;

              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.7,
                    delay: index * 0.1,
                  }}
                  className="bg-[color:var(--p10-ivory)] p-10 md:p-14"
                >
                  <Icon className="w-8 h-8 text-[color:var(--p10-royal)]" />

                  <h3 className="font-serif-display text-3xl md:text-4xl mt-6 leading-tight">
                    {pillar.title}
                  </h3>

                  <p className="mt-4 text-[color:var(--p10-ink)]/70 leading-relaxed">
                    {pillar.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-24 md:py-32 bg-[color:var(--p10-ink)] text-white relative border-y border-white/10">
        <div className="grain" />

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          <Eyebrow dark>Network partners</Eyebrow>

          <h2 className="font-serif-display text-4xl md:text-5xl mt-6 leading-[1.02] max-w-3xl">
            Intentionally{" "}
            <em className="text-[color:var(--p10-warm)]">
              non-exclusive
            </em>
            .
          </h2>

          <p className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed">
            We work with multiple network partners rather than a single provider,
            so your payment reach is future-proofed as the market evolves.
          </p>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {PARTNERS.map((partner, index) => (
              <motion.div
                key={partner}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
                className="bg-[color:var(--p10-ink)] h-32 md:h-40 flex items-center justify-center px-6 group hover:bg-[color:var(--p10-ink-2)] transition-colors"
                data-testid={`partner-${index}`}
              >
                <div className="text-center">
                  <div className="font-mono-tech text-[10px] tracking-[0.24em] text-[color:var(--p10-electric)] opacity-70 group-hover:opacity-100 transition-opacity">
                    PARTNER
                  </div>

                  <div className="mt-2 font-serif-display text-lg md:text-xl text-white/85 group-hover:text-white transition-colors">
                    {partner}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-10 text-xs font-mono-tech text-white/40 tracking-wide max-w-2xl leading-relaxed">
            Placeholder representation. Specific partner names and logos will be
            added as agreements permit. Nothing on this page represents an
            exclusive relationship with any single provider.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.02] max-w-2xl">
            Curious which corridors we already cover?
          </h2>

          <Link
            to="/contact"
            data-testid="partnerships-cta"
            className="btn-square btn-primary"
          >
            Talk to our team
            <ArrowUpRight className="ml-2 w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}